import React, { useCallback, useEffect, useRef, useState } from "react";
import { FileText, Loader2, ReceiptText, User } from "lucide-react";

import { Button } from "../../components/ui/button";
import { useEditorMode, useHideGlobalHeader } from "../../contexts/PageTitleContext";
import {
  CALL_PHASE,
  NATIVE_PRESENCE,
  getCallState,
  hangupCall,
  disposeCall,
  getDispositionOptions,
  observeNativePresence,
  pausePresence,
  startDialing,
  getWorkspaceSession,
  logoutCampaign,
} from "../../lib/telephonyCampaignLoginApi";
import { logoutRefusalMessage } from "./campaignLogoutCopy";
import { hangupRefusalMessage } from "./callLifecycleCopy";
import { LEAD_DETAILS_DEFERRED } from "./workspaceFrameCopy";
import WorkspacePreviewGate from "./components/WorkspacePreviewGate";
import WorkspaceCallBar from "./components/WorkspaceCallBar";
import WorkspaceContextDock from "./components/WorkspaceContextDock";
import WorkspaceFrame from "./components/WorkspaceFrame";
import { KPI_UNAVAILABLE_NOTE, PRODUCTION_KPI_ITEMS } from "./components/WorkspaceKpiRegion";
import WorkspaceMainCard from "./components/WorkspaceMainCard";
import WorkspaceOperationalRow, { CampaignContext } from "./components/WorkspaceOperationalRow";
import WorkspaceScriptRunner from "./components/WorkspaceScriptRunner";
import { DEFAULT_PRODUCTION_TOOL } from "./components/WorkspaceToolPanels";
import useWorkspaceToolData from "./useWorkspaceToolData";
import useOwnedCallCustomer from "./useOwnedCallCustomer";
import QuotingPanel from "./components/panels/QuotingPanel";
import WorkspaceProductionLayout from "./components/WorkspaceProductionLayout";
import { presenceFailureMessage } from "./presenceFailureCopy";
import {
  PRODUCT_READY_LABEL,
  STRANDED_RETAINED_LEAD_LABEL,
  STRANDED_RETAINED_LEAD_NOTICE,
  activeStatusLabel,
} from "./productStateCopy";
import {
  AUDIO_LOST_NOTICE,
  AUDIO_RECOVERY_NOTICE,
  ENABLE_AUDIO_PROMPT,
  audioFailureMessage,
} from "./audioStatusCopy";
import { useAgentMediaLeg } from "../../hooks/useAgentMediaLeg";
import { AudioLifecycleState, requiresExplicitAudioRecovery } from "../../telephony/audioLifecycle";

function formatAvailableLeads(availableLeads) {
  if (availableLeads === null || availableLeads === undefined) return "—";
  return availableLeads.toLocaleString();
}

/**
 * BB-8 work order Stage 1-6: product state names below are exactly
 * WORKSPACE-STATE-001's own canonical vocabulary -- never "Ready" as
 * the target of Start Dialing (WORKSPACE-STATE-001 §6: "Product Ready
 * is not a direct alias for provider or Telephony Presence `Ready`").
 * READY here means exactly what WORKSPACE-STATE-001 defines it as:
 * "Campaign login is complete and Start Dialing has not been
 * confirmed" -- this is BB-6's own already-proven target, unchanged by
 * this addition. STARTING/ACTIVE/PAUSING/PAUSED are this slice's own
 * additions, each named for the exact WORKSPACE-STATE-001 product
 * state it represents.
 */
/**
 * BB-12C -- the backend's post-disposition resume vocabulary, mirrored.
 *
 * Duplicated deliberately rather than inferred, for the same reason
 * `ReadinessReason` is: a value this build does not recognise must fall
 * through to the safe paused state, and that only works if the
 * recognised set is written down. Exactly one value may render Active.
 */
export const RESUME_OUTCOME = Object.freeze({
  RESUMED: "resumed",
  PAUSED_BY_REQUEST: "paused_by_request",
  NOT_ATTEMPTED: "not_attempted",
  MEDIA_NOT_READY: "media_not_ready",
  REFUSED: "refused",
  UNAVAILABLE: "unavailable",
});

const PRESENCE_STATE = Object.freeze({
  READY: "ready",
  STARTING: "starting",
  ACTIVE: "active",
  PAUSING: "pausing",
  PAUSED: "paused",
  // BB-11 call lifecycle. Extends this shell's own state machine rather
  // than adding a second call-state store: workspaceStateStore.js is
  // the fixture-driven Boundary-A store for AgentWorkspaceShell, and
  // this Production shell deliberately does not import it.
  //
  // There is no Dialing and no Ringing. Neither is a state native's
  // outbound RATIO lifecycle ever writes, so neither can be shown to an
  // agent without inventing something the provider never said.
  CONNECTED: "connected",
  HANGING_UP: "hanging_up",
  WRAP_UP: "wrap_up",
  // BB-6L: a campaign logout intent awaiting authoritative
  // confirmation. The name matches workspaceStateStore's own `Ending`,
  // which anticipated this step before it existed.
  ENDING: "ending",
  // BB-14d2c2. Campaign login succeeding no longer means Product Ready:
  // the agent has a native session but no verified browser audio, and
  // Start Dialing must not be offered until both exist. These two are
  // deliberately distinct from PAUSED, which means an agent who was
  // already Active chose to stop -- a completely different situation
  // with a completely different remedy.
  CONNECTING_AUDIO: "connecting_audio",
  AUDIO_FAILED: "audio_failed",
  // BB-14g. The backend established an acquisition obligation, could
  // not keep it, and exhausted its bounded re-establishment budget.
  //
  // Deliberately NOT Paused. Native may still be sitting at QUEUE --
  // Production produced exactly that -- and `VDADpause`/`VDADready`
  // both carry `status NOT IN('QUEUE','INCALL')`, so presenting Paused
  // would claim a provider state that never happened and point the
  // agent at two actions that would match zero rows. It is also not
  // Active: no call can be assigned in this session. Recovery is
  // campaign logout, which native honours unconditionally.
  ACQUISITION_FAILED: "acquisition_failed",
  // AW-2B3. The owned call ended, the provider session is gone, and the
  // native lead the call claimed is still unresolved.
  //
  // Distinct from every state above it, and each for its own reason.
  // Not ACTIVE or READY: there is no provider session to be eligible
  // in. Not PAUSED: that claims a `vicidial_live_agents` row this agent
  // no longer has. Not CONNECTED or HANGING_UP: the call is over. Not
  // WRAP_UP: that offers a disposition the backend has proven it cannot
  // send. Not ACQUISITION_FAILED: a call *was* assigned and did
  // connect, and that state's remedy copy -- log out and back in to
  // recover -- is exactly wrong here, because a new session leaves this
  // lead untouched.
  //
  // The only control it offers is campaign logout.
  RETAINED_LEAD_STRANDED: "retained_lead_stranded",
});

/**
 * AW-2B3 -- the canonical set of product states that offer Campaign
 * Logout, and the single source of truth for both halves of that
 * decision: whether the control renders, and whether the handler acts.
 *
 * **Why this is one list and not two.** It used to be two, and they
 * drifted twice. `handleLogoutCampaign` carried its own inline
 * allow-list while the button carried another, so a state added to the
 * render condition and not to the guard produced a *rendered but inert*
 * control: the click returned silently on the guard, issuing no
 * request, changing no state and surfacing no error. BB-14g shipped
 * that for `acquisition_failed` -- whose own documentation names
 * campaign logout as the only recovery it has -- and AW-2B3 PR-1
 * reproduced it for `retained_lead_stranded`, which was found in
 * Production when an agent on the stranded terminal screen clicked the
 * one control offered and nothing happened.
 *
 * Collapsing the two lists is the repair. Appending the two missing
 * states to the guard would have fixed today's symptom and left the
 * same trap for the next state.
 *
 * **Deliberately not broadened.** Exactly the six states already
 * characterized as intentionally logout-offered. In particular
 * `ACTIVE`, `CONNECTED`, `HANGING_UP` and `WRAP_UP` are absent: a live
 * or awaiting-disposition call is the backend's to refuse, and
 * offering logout there would invite a click the server must reject.
 * `ENDING` is absent because it *is* a logout already in flight, and
 * renders its own pending control.
 */
const LOGOUT_OFFERED_STATES = Object.freeze([
  PRESENCE_STATE.READY,
  PRESENCE_STATE.PAUSED,
  PRESENCE_STATE.CONNECTING_AUDIO,
  PRESENCE_STATE.AUDIO_FAILED,
  // BB-14g: the only recovery this state has. Native honours
  // `userLOGout` unconditionally, unlike Pause and Start.
  PRESENCE_STATE.ACQUISITION_FAILED,
  // AW-2B3: the only control this state offers at all. Logout ends the
  // session; it does not resolve the stranded lead, and the notice says
  // so.
  PRESENCE_STATE.RETAINED_LEAD_STRANDED,
]);

/**
 * Whether Campaign Logout is offered in this product state.
 *
 * Exported so the contract can be asserted behaviourally -- a test can
 * iterate the canonical set and prove every member both renders the
 * control and acts on a click, rather than restating the list and
 * drifting from it in a third place.
 */
export function logoutCampaignOffered(presenceState) {
  return LOGOUT_OFFERED_STATES.includes(presenceState);
}

export { LOGOUT_OFFERED_STATES };

/**
 * PR-A -- the user-visible call condition shown in the bottom call bar.
 *
 * WHY A MAP AND NOT INLINE TERNARIES
 * ----------------------------------
 * These three strings already exist as the header's own status chips.
 * Writing them a second time inline is how two surfaces start saying
 * different words for one state. Naming them once, here, keeps the bar
 * and the chip in agreement by construction, and lets a test assert the
 * agreement directly.
 *
 * WHY IT RETURNS null RATHER THAN A RESTING LABEL
 * -----------------------------------------------
 * Every other presence state means there is no current call, and the
 * bar says so itself (`CALL_BAR_NO_CALL`). Returning a label here for
 * READY or ACTIVE would let the bar claim a call condition for a state
 * that has no call -- the exact collapse of campaign status into call
 * condition that S15.2 forbids.
 */
const CALL_CONDITION_LABELS = Object.freeze({
  [PRESENCE_STATE.CONNECTED]: "Connected",
  [PRESENCE_STATE.HANGING_UP]: "Hanging Up\u2026",
  [PRESENCE_STATE.WRAP_UP]: "Wrap-up",
});

export function callConditionLabel(presenceState) {
  return CALL_CONDITION_LABELS[presenceState] ?? null;
}

// Product states in which the backend owns the truth and must be
// polled. Acquisition (Active) is included because a call can be
// claimed at any moment while the agent is eligible.
const BACKEND_AUTHORITATIVE_STATES = Object.freeze([
  PRESENCE_STATE.ACTIVE,
  PRESENCE_STATE.CONNECTED,
  PRESENCE_STATE.HANGING_UP,
  PRESENCE_STATE.WRAP_UP,
  // BB-14g: keep polling. Every repeat re-asserts the same failure, so
  // the state is stable rather than sticky -- and if the backend ever
  // does say something else, the agent is not left looking at a frozen
  // screen the shell has stopped reconciling.
  PRESENCE_STATE.ACQUISITION_FAILED,
  // AW-2B3: keep polling, for the same reason. The state is terminal
  // for this session but it is still the backend's answer, and a shell
  // that stopped reconciling could not show a correction if one came.
  // Note what is deliberately NOT done: this state is absent from
  // NATIVE_PRESENCE_RECONCILABLE_STATES, because reconciling product
  // state against a `vicidial_live_agents` row that does not exist
  // would be asking the provider about a session it has already lost.
  PRESENCE_STATE.RETAINED_LEAD_STRANDED,
]);

/**
 * BB-12 -- bounded disposition failure copy.
 *
 * Native's own text carries operator-configured URLs and free-form
 * messages and never crosses the API boundary, so this maps the
 * backend's bounded reasons onto something an agent can act on. The
 * default is deliberately plain rather than alarming: the call is
 * already over, and the remedy is almost always to try again.
 */
function dispositionFailureMessage(result) {
  const reason = result?.refusal || result?.verification || result?.signal;
  switch (reason) {
    case "unsupported_disposition":
      return "That disposition isn't available for this campaign.";
    case "not_in_wrap_up":
      return "This call is no longer awaiting completion.";
    case "lead_still_held":
    case "agent_not_paused":
    case "lead_status_mismatch":
      return "The provider hasn't confirmed this yet. Try again.";
    case "observation_unavailable":
      return "Couldn't confirm with the dialer. Try again.";
    case "session_changed":
      return "This session has changed. Log out of the campaign to continue.";
    case "native_context_unavailable":
      // BB-12.3. The call is genuinely awaiting disposition, but the
      // backend no longer holds the native context a first disposition
      // needs -- the usual cause is a restart during Wrap-up. Retrying
      // cannot help, so this deliberately does not say "try again".
      return "This call can't be completed from here. Contact your administrator.";
    default:
      return "This didn't complete. Try again.";
  }
}

const DEFERRED_PANEL_CLASS =
  "flex min-h-0 flex-1 flex-col items-center justify-center px-6 py-10 text-center";

const CALL_STATE_POLL_MS = 2000;

// BB-14e. Native presence changes far less often than call state, and
// this is a provider read -- polling it at the call-state cadence would
// triple provider load for no benefit.
const NATIVE_PRESENCE_POLL_MS = 5000;

// Product states in which native presence is authoritative. Deliberately
// excludes every owned-call state: once BB-9 has claimed a call,
// BB-9c/BB-11 own Connected and Wrap-up, and a second authority
// reconciling the same transition could tear the agent out of a live
// call. Also excludes the pending states (STARTING/PAUSING/ENDING),
// whose own command result is the authority.
const NATIVE_PRESENCE_RECONCILABLE_STATES = Object.freeze([
  PRESENCE_STATE.ACTIVE,
  PRESENCE_STATE.PAUSED,
]);

// P2-H2. How often a workspace that has no other recurring telephony
// request asks the backend about its own session, purely so BB-14i has a
// read to recover on.
//
// Slower than every other poll on purpose. This is not reconciliation --
// nothing on screen depends on the answer -- and the bound it has to
// satisfy is the S1 lease TTL, which is 60 seconds. A 15-second cadence
// recovers an orphaned session within roughly one TTL, the same order as
// the 4-second readiness poll achieves, at a third of the provider cost
// of the 5-second presence poll.
const SESSION_RECOVERY_POLL_MS = 15000;

// P2-H2. The product states that generate no other BB-14i-triggering
// request, and therefore need this one.
//
// Today that is exactly Product Ready, and only after a P2-R1
// restoration. A *normal* login reaches READY with `started` true, so
// `useAgentMediaLeg` is already polling `/agent-media/readiness` every 4
// seconds -- BB-14i's primary trigger. A restored workspace deliberately
// never calls `connectAudio()`, so `started` stays false, that poll never
// starts, and READY is in neither BACKEND_AUTHORITATIVE_STATES nor
// NATIVE_PRESENCE_RECONCILABLE_STATES. Such a workspace polls nothing at
// all, which is the P2-H2 blackout.
//
// Kept as its own list rather than folded into
// NATIVE_PRESENCE_RECONCILABLE_STATES. Adding READY there would have been
// a smaller diff and was rejected: `reconcileNativePresence` maps an
// observed native PAUSED onto PRESENCE_STATE.PAUSED, and Product Ready
// *is* natively paused, so the first poll would silently convert every
// restored Product Ready into Product Paused -- swapping Start Dialing
// (gated on READY) for Resume (gated on PAUSED) and relabelling
// "Logged In - Paused" as "Paused". That is a product state transition,
// not a polling change, and H2 is not authorized to make it.
const SESSION_RECOVERY_POLL_STATES = Object.freeze([PRESENCE_STATE.READY]);

/**
 * BB6 Production activation: the real Campaign Login vertical slice,
 * independently renderable in Production for an allowlisted account.
 *
 * BB-8 work order Stage 1-6 addition: extends the prior static
 * Logged In-Paused terminal banner with a real Start Dialing action
 * and manual Pause/Resume, wired to the real backend PRESENCE
 * executor (`POST /telephony/bb6/start-dialing`, `POST
 * /telephony/bb6/pause`). Never shows Active optimistically -- a
 * pending action shows "Starting..."/"Pausing..." until the backend's
 * own fresh, observation-gated confirmation returns; a failure or
 * timeout reverts to the last confirmed state and shows the reason,
 * never a false Active.
 *
 * Still deliberately does NOT import fakeRuntime.js,
 * workspaceViewModel.js, workspaceFixtures.js, or any of the fixture-
 * driven post-login body components (WorkspaceOperationalRow/Grid/
 * MainCard/AgentToolsCard/CallControls) -- this slice's own scope ends
 * at Active/Paused; no Dialing/Ringing/Connected/Wrap-up, no fixture
 * lead/script/quoting state, no later-slice call controls. See
 * AgentWorkspaceShell.jsx (untouched, Development/Boundary-A only) for
 * the fixture-driven counterpart this deliberately does not reuse or
 * promote into Production.
 */
export default function AgentWorkspaceProductionShell() {
  useEditorMode(true);
  useHideGlobalHeader(true);

  const [activeCampaign, setActiveCampaign] = useState(null);
  /**
   * P2-R1 -- whether session restoration has answered yet.
   *
   * Starts unresolved, and the Campaign Login gate is deliberately not
   * rendered until it settles. Showing an actionable Login button while
   * we do not yet know whether a session exists is how an agent ends up
   * issuing a second login over a live one.
   */
  const [sessionResolved, setSessionResolved] = useState(false);
  const [presenceState, setPresenceState] = useState(PRESENCE_STATE.READY);
  const [presenceError, setPresenceError] = useState(null);

  // BB-12. Offered choices come from the backend, which intersects
  // provider-selectable with what BB-12 can actually execute -- the
  // browser is never the authority on what a disposition means.
  const [dispositionOptions, setDispositionOptions] = useState({});
  const [dispositionInFlight, setDispositionInFlight] = useState(false);
  /**
   * BB-12C -- "Pause after disposition", one-shot and client-held.
   *
   * Deliberately not read from `vicidial_campaigns.pause_after_next_call`:
   * native keeps the agent's per-call choice browser-side too, and
   * coupling this to campaign configuration MissionAssist does not own
   * would be a dependency this slice does not need.
   */
  const [pauseAfterDisposition, setPauseAfterDisposition] = useState(false);
  const [dispositionError, setDispositionError] = useState(null);
  const [callError, setCallError] = useState(null);
  const [leadRetained, setLeadRetained] = useState(false);
  /**
   * AW-1 H5 containment. True once the backend reported an ENDED
   * call -- a call that stopped without a verified native release --
   * and nothing since has re-established that a further call can
   * still be assigned.
   *
   * While true, Active renders WITHOUT the waiting claim. Native may
   * be sitting somewhere that will never produce another assignment,
   * and 'Active / Waiting' would assert precisely the thing in
   * doubt. Presentation only: AW-1 issues no resume, changes no
   * backend presence behavior, and this flag gates no transition.
   */
  const [waitingUnconfirmed, setWaitingUnconfirmed] = useState(false);
  // Guards a Hangup already in flight. A ref, not state: it must be
  // true synchronously on the first click, before any re-render, or a
  // double-click sends two teardowns -- and two teardowns can race a
  // replacement call.
  const hangupInFlight = useRef(false);

  /**
   * PR-A-VIS-1 -- which center tab is shown.
   *
   * Presentation only. It selects a tab and decides nothing else: the
   * Script runner is force-mounted, so switching away from Script hides
   * it without unmounting it, and no tab can start, stop or alter a
   * Script Run. Script is the default because it is the only tab with a
   * live capability (S5.5).
   */
  const [activeCenterTab, setActiveCenterTab] = useState("script");

  /**
   * AW-VIS-3. The Agent Tools rail's two pieces of presentation state,
   * owned here exactly as the authoritative original shell owns them
   * (`AgentWorkspaceShell`, commit 23d500e27): which tool is selected,
   * and whether the rail is expanded.
   *
   * These are the only two hooks this slice adds, and they are
   * deliberately inert with respect to everything else. Neither is read
   * by any effect, poll, predicate or request in this file; neither
   * appears in a dependency array; and no telephony, media or Script
   * code path can observe either one. Changing a tool re-renders the
   * right-hand region and nothing else.
   *
   * That matters most during a call. `WorkspaceMainCard` keeps its
   * position in the element tree across every value these can take, so
   * React reconciles rather than remounts, and the force-mounted Script
   * runner survives a collapse, an expand and any number of tool
   * switches with its run intact. The suite asserts that by node
   * identity at both Connected and Wrap-up, not by re-render count.
   */
  const [activeTool, setActiveTool] = useState(DEFAULT_PRODUCTION_TOOL);
  const [isToolsExpanded, setIsToolsExpanded] = useState(true);

  /**
   * Selecting a tool while the rail is collapsed re-expands it --
   * the original shell's own behaviour. Without it the agent clicks a
   * tool, the selection changes, and nothing visible happens.
   */
  const handleSelectTool = useCallback((toolKey) => {
    setActiveTool(toolKey);
    setIsToolsExpanded(true);
  }, []);

  const handleToggleTools = useCallback(() => {
    setIsToolsExpanded((expanded) => !expanded);
  }, []);

  /**
   * AW-LDQ-003. The Script run the Agent Tools read from.
   *
   * Published by `WorkspaceScriptRunner`, which already owns it -- the
   * tools never resolve a run of their own. `startEffectiveScriptRun`
   * still has exactly one caller, `shouldStartRun` still admits exactly
   * one phase, and nothing here can begin, extend or end a run. This
   * holds an identity and nothing else.
   */
  const [scriptRun, setScriptRun] = useState(null);

  const handleRunChange = useCallback((next) => {
    // Identity-only comparison: a re-publication of the same run must
    // not clear and refetch the tools mid-call.
    setScriptRun((current) => {
      if (current?.runId === (next?.runId ?? null)) return current;
      return next
        ? {
            runId: next.runId,
            scriptId: next.scriptId,
            // AW-2C-008. The proof the customer snapshot publishes
            // against. Held beside the run identity because it names
            // the same owned call.
            callCorrelationId: next.callCorrelationId ?? null,
          }
        : null;
    });
  }, []);

  const toolData = useWorkspaceToolData({
    tool: activeTool,
    runId: scriptRun?.runId ?? null,
  });

  /**
   * AW-2C-005. The owned call's customer, fetched once and shared.
   *
   * Mounted HERE rather than inside either consumer, which is what
   * makes "one request per call" structural: the Lead panel and the
   * embedded Script runner both receive this object as a prop, so
   * switching tools cannot re-enter it and a runner remount cannot
   * either. Two fetches would also be two answers that could disagree
   * after a release-and-reclaim -- one surface naming the previous
   * customer while the other names the current one.
   *
   * Keyed on the same `runId` the tools use. The run is bound
   * one-to-one to the owned call (AW-2B3.1), so this clears and
   * refetches exactly when the call changes and -- because the run
   * survives Wrap-up -- holds its value while the agent dispositions.
   */
  /**
   * AW-2C-008. Eligibility -- not the run id -- is what starts the
   * customer fetch.
   *
   * `Connected` issues it; `Hanging-up` and `Wrap-up` keep it, which is
   * what preserves the customer through retained Wrap-up while the
   * agent chooses a disposition. Anything else clears.
   *
   * The run id and the correlation still govern PUBLICATION: nothing
   * reaches a panel or a Script token until the two server-issued
   * correlation identifiers match.
   */
  const customerEligible =
    presenceState === PRESENCE_STATE.CONNECTED ||
    presenceState === PRESENCE_STATE.HANGING_UP ||
    presenceState === PRESENCE_STATE.WRAP_UP;

  const customerData = useOwnedCallCustomer({
    eligible: customerEligible,
    runId: scriptRun?.runId ?? null,
    correlationId: scriptRun?.callCorrelationId ?? null,
  });

  /**
   * BB-14d2c2. One media controller per active campaign session,
   * owned by the existing `useAgentMediaLeg` architecture rather than a
   * competing lifecycle owner: it already holds the client outside
   * React state so a re-render cannot disturb a live leg.
   */
  const media = useAgentMediaLeg();
  const {
    audioState,
    readiness: audioReadiness,
    failure: audioFailure,
    playbackBlocked,
    connectAudio,
    enableAudio,
    retryAudio,
    stopAudio,
  } = media;

  /**
   * Fold one backend call-state snapshot into product state. The
   * backend is authoritative for every call-bearing phase; this maps,
   * it never infers.
   */
  const applyCallSnapshot = useCallback((snapshot) => {
    const phase = snapshot?.phase;
    setLeadRetained(Boolean(snapshot?.lead_retained));

    if (phase === CALL_PHASE.CONNECTED) {
      // An assigned call is the strongest possible evidence that the
      // session is still assignable, so any earlier ENDED doubt is
      // now genuinely resolved rather than merely stale.
      setWaitingUnconfirmed(false);
      setPresenceState(PRESENCE_STATE.CONNECTED);
      return;
    }
    if (phase === CALL_PHASE.HANGING_UP) {
      setPresenceState(PRESENCE_STATE.HANGING_UP);
      return;
    }
    if (phase === CALL_PHASE.WRAP_UP) {
      setPresenceState(PRESENCE_STATE.WRAP_UP);
      return;
    }
    if (phase === CALL_PHASE.ACQUISITION_FAILED) {
      // BB-14g. Before this branch existed, `acquisition_failed` fell
      // through to the IDLE/ACQUIRING default below, which preserves
      // the current state -- so an Active agent stayed Active while the
      // backend knew no call could ever arrive, and the 2-second poll
      // discarded that answer every time.
      //
      // Read-side only: this maps what the backend reported and
      // dispatches nothing. It must never issue a presence transition
      // merely because the phase was observed.
      setPresenceState(PRESENCE_STATE.ACQUISITION_FAILED);
      setCallError(
        "Call assignment failed. No new calls can be assigned in this session. Log out of the campaign and log back in to recover.",
      );
      return;
    }
    if (phase === CALL_PHASE.RETAINED_LEAD_STRANDED) {
      // AW-2B3. Inert until the backend emits this phase; Production
      // never has. Placed above the ENDED branch rather than below it
      // so that neither can shadow the other as this chain grows.
      //
      // Read-side only, exactly like the ACQUISITION_FAILED branch: it
      // maps what the backend reported and dispatches nothing. It must
      // never issue a presence transition merely because the phase was
      // observed.
      //
      // `callError` is cleared rather than set. The chip and the notice
      // below carry the whole message, and a stale "the call ended
      // unexpectedly" banner left over from an earlier ENDED poll would
      // sit alongside them telling the agent to check with a supervisor
      // *before dialing again* -- which contradicts a session that is
      // over. `waitingUnconfirmed` is deliberately left alone: it only
      // chooses between two Active labels, and no Active label renders
      // here.
      setPresenceState(PRESENCE_STATE.RETAINED_LEAD_STRANDED);
      setCallError(null);
      return;
    }
    if (phase === CALL_PHASE.ENDED) {
      // Never Wrap-up. ENDED means the call is no longer ours and
      // nothing verified a native release -- surfacing it as Wrap-up
      // would assert a release that never happened.
      //
      // AW-1: for the same reason it is not Wrap-up, it is not
      // 'Waiting' either. The state stays Active because the session
      // is still logged in and dialing, but the waiting claim is
      // withheld until something re-establishes assignability.
      setWaitingUnconfirmed(true);
      setPresenceState(PRESENCE_STATE.ACTIVE);
      setCallError(
        "The call ended unexpectedly. If you were on a call, check with your supervisor before dialing again.",
      );
      return;
    }
    // IDLE / ACQUIRING: waiting for a call. Existing Active UI.
    setPresenceState((current) =>
      current === PRESENCE_STATE.CONNECTED ||
      current === PRESENCE_STATE.HANGING_UP ||
      current === PRESENCE_STATE.WRAP_UP
        ? PRESENCE_STATE.ACTIVE
        : current,
    );
  }, []);

  // Reconciliation. Runs on mount -- so a refresh during Connected,
  // Hanging Up or Wrap-up reconstructs from the backend rather than
  // resetting to Active -- and then polls while the backend owns the
  // truth. A failed poll is left alone deliberately: a transient
  // network blip must not move an agent out of Connected.
  const reconcile = useCallback(async () => {
    try {
      applyCallSnapshot(await getCallState());
    } catch (error) {
      // Backend unreachable or the lifecycle flag is off. Hold the last
      // confirmed state.
    }
  }, [applyCallSnapshot]);

  /**
   * P2-R1 -- restore the workspace from durable session ownership.
   *
   * **The defect this closes.** `activeCampaign` lived only here, and
   * the operational shell rendered on `activeCampaign || hasOwnedCall`
   * -- so a refresh while Product Ready or Active dropped the agent to
   * the Campaign Login gate while native still had them logged in and
   * dialable, with no Pause and no Campaign Logout control.
   *
   * **Observation, never adoption.** This issues no Login, Start
   * Dialing, Resume, Pause or Logout, and the backend answers only from
   * an S1 lease this authenticated agent already owns. A reload cannot
   * change native presence.
   *
   * Presence is taken from MissionAssist's own lifecycle state rather
   * than a native read: the S1 lease is continuously reconciled against
   * native by the S2 beat, so it is both durable and native-truthful.
   * Owned-call phases stay authoritative -- `applyCallSnapshot`
   * preserves the current state for IDLE/ACQUIRING and overrides it for
   * Connected/Hanging Up/Wrap-up, which is the precedence we want.
   */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const session = await getWorkspaceSession();
        if (cancelled || !session?.session || !session?.campaign_id) return;

        setActiveCampaign((current) =>
          current || {
            campaign_id: session.campaign_id,
            campaign_name: session.campaign_name ?? session.campaign_id,
            // Deliberately absent: `available_leads` is display-only,
            // costs a second provider read, and the agent-facing figure
            // is a known-incorrect sum (P2-R2). The next campaign fetch
            // supplies it; until then an em dash is the honest answer.
            available_leads: null,
          },
        );

        setPresenceState((current) =>
          current === PRESENCE_STATE.CONNECTED ||
          current === PRESENCE_STATE.HANGING_UP ||
          current === PRESENCE_STATE.WRAP_UP
            ? current
            : session.lifecycle_state === "active"
              ? PRESENCE_STATE.ACTIVE
              : PRESENCE_STATE.READY,
        );
      } catch (error) {
        // Unreachable backend, or the feature gate is off. Fall through
        // to the Campaign Login gate exactly as before this slice.
      } finally {
        if (!cancelled) setSessionResolved(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    reconcile();
  }, [reconcile]);

  useEffect(() => {
    if (!BACKEND_AUTHORITATIVE_STATES.includes(presenceState)) return undefined;
    const timer = setInterval(reconcile, CALL_STATE_POLL_MS);
    return () => clearInterval(timer);
  }, [presenceState, reconcile]);

  /**
   * BB-14e -- reconcile with native VICIdial presence.
   *
   * Production showed an agent sitting at Active while VICIdial had
   * already returned them to PAUSED after a DROP/AMD call. Nothing here
   * observed native status: the only poll was BB-11 call state, which
   * correctly reported "no owned call" and left the UI untouched.
   *
   * Observation only -- `observeNativePresence` dispatches nothing. It
   * runs only in ACTIVE/PAUSED, never during an owned call or a pending
   * command, and `unknown` is ignored rather than rendered, so a provider
   * fault can never fabricate a transition.
   */
  const reconcileNativePresence = useCallback(async () => {
    if (!activeCampaign) return;
    const observed = await observeNativePresence(activeCampaign.campaign_id);
    if (observed === NATIVE_PRESENCE.UNKNOWN) return;
    setPresenceState((current) => {
      // Re-checked inside the setter: the state may have moved between
      // the request being issued and its answer arriving.
      if (!NATIVE_PRESENCE_RECONCILABLE_STATES.includes(current)) return current;
      if (observed === NATIVE_PRESENCE.PAUSED) return PRESENCE_STATE.PAUSED;
      if (observed === NATIVE_PRESENCE.ACTIVE) return PRESENCE_STATE.ACTIVE;
      return current;
    });
  }, [activeCampaign]);

  useEffect(() => {
    if (!NATIVE_PRESENCE_RECONCILABLE_STATES.includes(presenceState)) return undefined;
    const timer = setInterval(reconcileNativePresence, NATIVE_PRESENCE_POLL_MS);
    return () => clearInterval(timer);
  }, [presenceState, reconcileNativePresence]);

  /**
   * P2-H2 -- keep BB-14i reachable from Product Ready.
   *
   * BB-14i recovers an orphaned S2 runtime after a backend restart, but
   * it is driven entirely by reads the browser already makes. A
   * P2-R1-restored Product Ready workspace makes none, so an expired
   * lease stayed expired and the agent's only remedy was a fresh
   * Campaign Login -- exactly what BB-14i exists to prevent.
   *
   * This is a *trigger*, not an observation. The response is deliberately
   * discarded: `/bb6/session` recovers as a side effect, and acting on
   * what it returns would be reconciliation this slice is not authorized
   * to add. Nothing on screen depends on it, so nothing on screen can be
   * moved by it -- the product state machine is untouched.
   *
   * `/bb6/session` rather than `/presence` or `/agent-media/readiness`:
   * all three invoke BB-14i, but this one is already the endpoint R1
   * mounts with and is scoped to the same authenticated principal, it
   * needs no media identity and so cannot emit the LEASE_SUPERSEDED
   * teardown readiness can, and it dispatches nothing to the provider.
   *
   * Gated on `activeCampaign`, so the Campaign Login gate -- a workspace
   * with no session to recover -- starts no telephony polling at all.
   */
  useEffect(() => {
    if (!activeCampaign) return undefined;
    if (!SESSION_RECOVERY_POLL_STATES.includes(presenceState)) return undefined;

    const trigger = async () => {
      try {
        await getWorkspaceSession();
      } catch (error) {
        // A trigger that cannot reach the backend is simply a tick that
        // recovered nothing. There is no user-visible contract here to
        // fail, and the next tick tries again.
      }
      // No `cancelled` guard and nothing after the await on purpose: the
      // answer is discarded, so there is no late write for an unmounted
      // component to make.
    };

    const timer = setInterval(trigger, SESSION_RECOVERY_POLL_MS);
    return () => clearInterval(timer);
  }, [activeCampaign, presenceState]);

  /**
   * BB-12 -- load the offered dispositions when, and only when, a call
   * is awaiting one. Outside Wrap-up the backend answers empty, so the
   * control cannot be rendered for a call that does not exist.
   */
  useEffect(() => {
    if (presenceState !== PRESENCE_STATE.WRAP_UP) {
      setDispositionOptions({});
      setDispositionError(null);
      return;
    }
    let cancelled = false;
    // Wrapped rather than chained directly: an options lookup that
    // cannot answer must leave the agent with no choices to click, not
    // a broken workspace during Wrap-up.
    Promise.resolve(getDispositionOptions())
      .then((options) => {
        if (!cancelled) setDispositionOptions(options || {});
      })
      .catch(() => {
        if (!cancelled) setDispositionOptions({});
      });
    return () => {
      cancelled = true;
    };
  }, [presenceState]);

  /**
   * BB-12 -- submit one disposition.
   *
   * **Never optimistic.** Wrap-up is left only when the backend reports
   * `completed`, which it does only after fresh provider state agreed
   * that the agent is paused, the lead is released, and the lead's own
   * status is the chosen one. A failure keeps the agent visibly in
   * Wrap-up with an actionable message, because the call genuinely is
   * still undispositioned.
   */
  async function handleDisposition(code) {
    if (dispositionInFlight) return;
    if (presenceState !== PRESENCE_STATE.WRAP_UP) return;
    setDispositionInFlight(true);
    setDispositionError(null);
    try {
      const result = await disposeCall(code, { pauseAfter: pauseAfterDisposition });
      if (result?.completed) {
        // BB-12C. Disposition and resume are two outcomes. The call is
        // dispositioned either way; what differs is where the agent
        // lands.
        //
        // Only `resumed` means native reached a verified READY *and*
        // acquisition restarted -- the one state that may render Active.
        //
        // Everything else is PAUSED, not READY. The distinction is
        // agent-facing and load-bearing: READY renders "Logged In —
        // Paused" with Start Dialing, which is the truthful description
        // of an agent who just logged in and has not dialed yet. An
        // agent who was Active, took a call and dispositioned it has
        // *been* dialing, so "Paused" with Resume is what actually
        // describes them -- whether they asked to pause or the resume
        // was refused. PAUSED is also the state BB-14e reconciles
        // against native (`NATIVE_PRESENCE_RECONCILABLE_STATES`), so a
        // wrong guess here gets corrected by the provider rather than
        // persisting; READY is never reconciled.
        setLeadRetained(false);
        setPresenceState(
          result.resume === RESUME_OUTCOME.RESUMED
            ? PRESENCE_STATE.ACTIVE
            : PRESENCE_STATE.PAUSED,
        );
        // One-shot: consumed by the disposition it applied to.
        setPauseAfterDisposition(false);
        return;
      }
      // Disposition failed and the agent stays in Wrap-up, so the
      // selection is retained -- resetting it here would silently
      // change the post-disposition behaviour they asked for on retry.
      setDispositionError(dispositionFailureMessage(result));
    } catch (error) {
      setDispositionError("This didn't complete. Try again.");
    } finally {
      setDispositionInFlight(false);
    }
  }

  async function handleHangup() {
    if (hangupInFlight.current) return;
    if (presenceState !== PRESENCE_STATE.CONNECTED) return;
    hangupInFlight.current = true;
    setCallError(null);
    setPresenceState(PRESENCE_STATE.HANGING_UP);
    try {
      const snapshot = await hangupCall();
      if (snapshot?.phase === CALL_PHASE.WRAP_UP) {
        // The only path to Wrap-up: the backend verified the native
        // release against fresh provider state. Never optimistic.
        applyCallSnapshot(snapshot);
        return;
      }
      applyCallSnapshot(snapshot);
      setPresenceState(PRESENCE_STATE.CONNECTED);
      setCallError(hangupRefusalMessage(snapshot?.refusal));
    } catch (error) {
      // Ambiguous: the request may or may not have landed. Stay
      // Connected and recoverable; the next poll settles it.
      setPresenceState(PRESENCE_STATE.CONNECTED);
      setCallError(hangupRefusalMessage(null));
    } finally {
      hangupInFlight.current = false;
    }
  }

  /**
   * BB-14d2c2. Campaign login succeeded natively -- the agent is Logged
   * In and natively paused -- but the browser has no audio yet, so the
   * Workspace enters Connecting Audio rather than Product Ready. Start
   * Dialing is not offered here, and the backend would refuse it anyway
   * through the BB-14d2b Start Dialing gate.
   *
   * This is also the closest point to the agent's own Campaign Login
   * gesture at which audio can be set up: the gate awaits the native
   * login round-trip before calling back, and acquiring a microphone
   * before that round-trip would prompt an agent whose campaign login
   * may still fail.
   */
  function handleLogin(campaign) {
    setActiveCampaign(campaign);
    setPresenceState(PRESENCE_STATE.CONNECTING_AUDIO);
    setPresenceError(null);
    connectAudio();
  }

  /**
   * Fold the audio lifecycle into product state.
   *
   * Only the pre-Active states are driven from here. Once an agent has
   * entered Active -- or a call -- native presence is the backend's to
   * own, and BB-14e owns what a media loss does to it; overwriting
   * ACTIVE from the browser would be this slice inventing the very
   * fail-safe pause it was told not to implement.
   */
  useEffect(() => {
    if (!activeCampaign) return;
    setPresenceState((current) => {
      if (
        current !== PRESENCE_STATE.CONNECTING_AUDIO &&
        current !== PRESENCE_STATE.AUDIO_FAILED &&
        current !== PRESENCE_STATE.READY
      ) {
        return current;
      }
      if (audioState === AudioLifecycleState.READY) return PRESENCE_STATE.READY;
      if (audioState === AudioLifecycleState.AUDIO_FAILED) return PRESENCE_STATE.AUDIO_FAILED;
      if (audioState === AudioLifecycleState.CONNECTING_AUDIO) {
        return PRESENCE_STATE.CONNECTING_AUDIO;
      }
      return current;
    });
  }, [audioState, activeCampaign]);

  async function handleRetryAudio() {
    setPresenceState(PRESENCE_STATE.CONNECTING_AUDIO);
    setPresenceError(null);
    await retryAudio();
  }

  async function handleLogoutCampaign() {
    // Campaign Logout, never App Logout: this ends the native VICIdial
    // session and returns the Workspace to Campaign Selection. It
    // touches no authentication state, so the user stays signed in.
    if (!activeCampaign) return;
    // AW-2B3. The same contract the button renders from, so a state can
    // never again offer this control and then refuse the click.
    if (!logoutCampaignOffered(presenceState)) return;
    const previous = presenceState;
    setPresenceState(PRESENCE_STATE.ENDING);
    setPresenceError(null);
    try {
      const result = await logoutCampaign(activeCampaign.campaign_id);
      if (result && result.logged_out) {
        // BB-6L already released the server-side session, including any
        // media identity. Stopping the browser leg here is local
        // cleanup only -- it stops the microphone and the audio sink so
        // the recording indicator does not stay lit, and its
        // exact-peer revoke is a convergent no-op if BB-6L got there
        // first. It runs after, never before: browser cleanup must
        // never be a precondition for authoritative logout.
        stopAudio();
        setActiveCampaign(null);
        setPresenceState(PRESENCE_STATE.READY);
        setPresenceError(null);
        setCallError(null);
        return;
      }
      // A refusal is a 200 -- stay where we were and explain why.
      setPresenceState(previous);
      setPresenceError(logoutRefusalMessage(result && result.outcome));
    } catch (error) {
      setPresenceState(previous);
      setPresenceError(logoutRefusalMessage(null));
    }
  }

  async function handleStartDialing() {
    if (!activeCampaign || presenceState !== PRESENCE_STATE.READY) return;
    // Second, independent check. The backend enforces the same rule
    // through the BB-14d2b Start Dialing gate and the d2 admission
    // fence; this one keeps the browser from sending a command it
    // already knows will be refused.
    if (audioState !== AudioLifecycleState.READY) return;
    setPresenceState(PRESENCE_STATE.STARTING);
    setPresenceError(null);
    try {
      const result = await startDialing(activeCampaign.campaign_id);
      if (result.state === "active") {
        // AW-1: the backend confirmed an assignable state, so a waiting
        // claim withheld after an earlier ENDED is now supported again.
        setWaitingUnconfirmed(false);
        setPresenceState(PRESENCE_STATE.ACTIVE);
        return;
      }
      setPresenceState(PRESENCE_STATE.READY);
      setPresenceError(presenceFailureMessage(result.failure_reason));
    } catch (error) {
      setPresenceState(PRESENCE_STATE.READY);
      setPresenceError(presenceFailureMessage(null));
    }
  }

  async function handlePause() {
    if (!activeCampaign || presenceState !== PRESENCE_STATE.ACTIVE) return;
    setPresenceState(PRESENCE_STATE.PAUSING);
    setPresenceError(null);
    try {
      const result = await pausePresence(activeCampaign.campaign_id);
      if (result.state === "paused") {
        setPresenceState(PRESENCE_STATE.PAUSED);
        return;
      }
      setPresenceState(PRESENCE_STATE.ACTIVE);
      setPresenceError(presenceFailureMessage(result.failure_reason));
    } catch (error) {
      setPresenceState(PRESENCE_STATE.ACTIVE);
      setPresenceError(presenceFailureMessage(null));
    }
  }

  async function handleResume() {
    if (!activeCampaign || presenceState !== PRESENCE_STATE.PAUSED) return;
    setPresenceState(PRESENCE_STATE.STARTING);
    setPresenceError(null);
    try {
      const result = await startDialing(activeCampaign.campaign_id);
      if (result.state === "active") {
        // AW-1: the backend confirmed an assignable state, so a waiting
        // claim withheld after an earlier ENDED is now supported again.
        setWaitingUnconfirmed(false);
        setPresenceState(PRESENCE_STATE.ACTIVE);
        return;
      }
      setPresenceState(PRESENCE_STATE.PAUSED);
      setPresenceError(presenceFailureMessage(result.failure_reason));
    } catch (error) {
      setPresenceState(PRESENCE_STATE.PAUSED);
      setPresenceError(presenceFailureMessage(null));
    }
  }

  // A refresh loses local campaign state but not the call: the backend
  // still owns it. Rendering the panel on an owned call is what makes
  // reconciliation reconstruct Connected/Wrap-up instead of dropping
  // the agent back to the Campaign Login gate mid-call.
  const hasOwnedCall =
    presenceState === PRESENCE_STATE.CONNECTED ||
    presenceState === PRESENCE_STATE.HANGING_UP ||
    presenceState === PRESENCE_STATE.WRAP_UP;

  /**
   * Media loss after the agent already passed Product Ready.
   *
   * d2c2 detects and surfaces it and stops nothing: BB-14e owns the
   * fail-safe native pause. The one thing that must not happen is the
   * Workspace continuing to present healthy audio, which this makes
   * impossible for every post-Ready state.
   */
  const audioLostAfterReady =
    Boolean(activeCampaign) &&
    audioState !== AudioLifecycleState.READY &&
    (presenceState === PRESENCE_STATE.ACTIVE ||
      presenceState === PRESENCE_STATE.STARTING ||
      presenceState === PRESENCE_STATE.PAUSING ||
      presenceState === PRESENCE_STATE.PAUSED ||
      hasOwnedCall);

  /**
   * BB-14d2c3. An expired media identity cannot recover on its own and
   * cannot be re-bootstrapped while it is still ACTIVE at the current
   * generation, so the agent needs the retry control -- which otherwise
   * renders only for `AUDIO_FAILED`, a state this condition never
   * reaches.
   *
   * Deliberately does not touch `presenceState`. Native presence is the
   * backend's to own once the agent is Paused or Active; this exposes a
   * control and corrects the copy, and changes no state machine.
   */
  const audioRecoveryRequired =
    Boolean(activeCampaign) &&
    requiresExplicitAudioRecovery({ audioState, readiness: audioReadiness });

  return (
    <main
      aria-labelledby="agent-workspace-heading"
      className="flex h-full min-h-0 w-full flex-col overflow-hidden text-slate-100"
      data-testid="agent-workspace-shell"
    >
      <h1 id="agent-workspace-heading" className="sr-only">
        Agent Workspace
      </h1>

      {activeCampaign || hasOwnedCall ? (
        <WorkspaceFrame
          operationalRow={
            /*
             * AW-VIS-2. The authoritative single operational row
             * (WORKSPACE-FEATURE-001 S5.1 layers 1-2, S8, S9; original
             * `WorkspaceOperationalRow` at commit 23d500e27; reference
             * `docs/design/prototypes/workspace-v1-reference.png`).
             *
             * WHAT CHANGED, AND WHY IT HAD TO
             * -------------------------------
             * AW-2A put this campaign section in a 380px body rail and
             * stacked its controls `w-full flex-col`, which is correct
             * in a 380px column. PR-A promoted the section to a
             * full-width header and left the stack untouched -- so the
             * same two classes that produced two neat buttons in a rail
             * produced full-width stacked Start Dialing / Logout
             * Campaign across the whole workspace. That is the visual
             * acceptance failure, and it is repaired here by giving the
             * controls their authoritative container: inline,
             * intrinsically sized, at the right edge of the same row as
             * the KPIs (S8.3 -- campaign identity, status and primary
             * action read as ONE conceptual group).
             *
             * WHAT DID NOT CHANGE
             * -------------------
             * Every control, testid, handler, eligibility predicate and
             * string below is untouched. The state machine is untouched.
             * `presenceState` still decides which control renders, and
             * each control still calls exactly the handler it called
             * before. This is composition only.
             */
            <WorkspaceOperationalRow
              kpis={PRODUCTION_KPI_ITEMS}
              kpiNote={KPI_UNAVAILABLE_NOTE}
              campaign={
                <section
                  aria-label="Campaign status"
                  className="flex flex-wrap items-center gap-x-4 gap-y-2"
                  data-testid="workspace-logged-in-paused"
                >
                  {activeCampaign ? (
                    <CampaignContext
                      campaignName={activeCampaign.campaign_name}
                      nameTestId="workspace-logged-in-campaign-name"
                    >
                      <span
                        className="text-xs text-slate-500"
                        data-testid="workspace-logged-in-available-leads"
                      >
                        {formatAvailableLeads(activeCampaign.available_leads)} available
                      </span>
                    </CampaignContext>
                  ) : null}

                {presenceState === PRESENCE_STATE.READY ? (
                  /*
                   * AW-1. Product Ready, not "Logged In - Paused".
                   *
                   * The provider IS natively paused here, but that is
                   * provider vocabulary: PRESENCE_STATE.READY and
                   * PRESENCE_STATE.PAUSED both map to a natively paused
                   * agent, and the product distinction between them (Start
                   * Dialing vs Resume) is entirely MissionAssist's. Nothing
                   * about that semantics changed -- only what the agent
                   * reads.
                   *
                   * The legacy testid is retained on the paragraph for one
                   * release so existing acceptance selectors keep resolving;
                   * the canonical id is on the inner span. Both address the
                   * same text.
                   */
                  <p
                    className="rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-1.5 text-sm font-medium text-amber-300"
                    data-testid="workspace-status-logged-in-paused"
                  >
                    <span data-testid="workspace-status-product-ready">{PRODUCT_READY_LABEL}</span>
                  </p>
                ) : null}
                {presenceState === PRESENCE_STATE.CONNECTING_AUDIO ? (
                  <p
                    className="rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-1.5 text-sm font-medium text-sky-300"
                    data-testid="workspace-status-connecting-audio"
                  >
                    Connecting Audio…
                  </p>
                ) : null}
                {presenceState === PRESENCE_STATE.AUDIO_FAILED ? (
                  <p
                    className="rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-1.5 text-sm font-medium text-red-300"
                    data-testid="workspace-status-audio-failed"
                  >
                    Audio connection failed
                  </p>
                ) : null}
                {presenceState === PRESENCE_STATE.STARTING ? (
                  <p
                    className="rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-1.5 text-sm font-medium text-amber-300"
                    data-testid="workspace-status-starting"
                  >
                    Starting…
                  </p>
                ) : null}
                {presenceState === PRESENCE_STATE.ACTIVE ? (
                  <p
                    className="rounded-[10px] border border-white/10 bg-emerald-500/10 px-3 py-1.5 text-sm font-medium text-emerald-300"
                    data-testid="workspace-status-active"
                    data-waiting-confirmed={String(!waitingUnconfirmed)}
                  >
                    {activeStatusLabel({ waitingConfirmed: !waitingUnconfirmed })}
                  </p>
                ) : null}
                {presenceState === PRESENCE_STATE.PAUSING ? (
                  <p
                    className="rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-1.5 text-sm font-medium text-amber-300"
                    data-testid="workspace-status-pausing"
                  >
                    Pausing…
                  </p>
                ) : null}
                {presenceState === PRESENCE_STATE.PAUSED ? (
                  <p
                    className="rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-1.5 text-sm font-medium text-amber-300"
                    data-testid="workspace-status-paused"
                  >
                    Paused
                  </p>
                ) : null}
                {presenceState === PRESENCE_STATE.CONNECTED ? (
                  <p
                    className="rounded-[10px] border border-white/10 bg-emerald-500/10 px-3 py-1.5 text-sm font-medium text-emerald-300"
                    data-testid="workspace-status-connected"
                  >
                    Connected
                  </p>
                ) : null}
                {presenceState === PRESENCE_STATE.HANGING_UP ? (
                  <p
                    className="rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-1.5 text-sm font-medium text-amber-300"
                    data-testid="workspace-status-hanging-up"
                  >
                    Hanging Up…
                  </p>
                ) : null}
                {presenceState === PRESENCE_STATE.WRAP_UP ? (
                  <p
                    className="rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-1.5 text-sm font-medium text-sky-300"
                    data-testid="workspace-status-wrap-up"
                  >
                    Wrap-up
                  </p>
                ) : null}
                {presenceState === PRESENCE_STATE.ACQUISITION_FAILED ? (
                  <p
                    className="rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-1.5 text-sm font-medium text-red-300"
                    data-testid="workspace-status-acquisition-failed"
                  >
                    Call assignment failed
                  </p>
                ) : null}
                {presenceState === PRESENCE_STATE.RETAINED_LEAD_STRANDED ? (
                  <p
                    className="rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-1.5 text-sm font-medium text-red-300"
                    data-testid="workspace-status-retained-lead-stranded"
                  >
                    {STRANDED_RETAINED_LEAD_LABEL}
                  </p>
                ) : null}
                </section>
              }
              controls={
                /*
                  The right-edge operational control group.

                  `flex-wrap items-center` and NOT `w-full flex-col`.
                  Controls are inline and intrinsically sized, which is
                  the authoritative arrangement and the direct repair of
                  the full-width stack. Nothing else about them moved.
                */
                <div
                  className="flex flex-wrap items-center justify-end gap-2"
                  data-testid="workspace-campaign-controls"
                >
                  {presenceState === PRESENCE_STATE.READY ? (
                    <Button type="button" onClick={handleStartDialing} data-testid="workspace-start-dialing-button">
                      Start Dialing
                    </Button>
                  ) : null}
                  {presenceState === PRESENCE_STATE.AUDIO_FAILED || audioRecoveryRequired ? (
                    <Button type="button" onClick={handleRetryAudio} data-testid="workspace-retry-audio-button">
                      Retry Audio
                    </Button>
                  ) : null}
                  {logoutCampaignOffered(presenceState) ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleLogoutCampaign}
                      data-testid="workspace-logout-campaign-button"
                    >
                      Logout Campaign
                    </Button>
                  ) : null}
                  {presenceState === PRESENCE_STATE.ENDING ? (
                    <Button type="button" disabled data-testid="workspace-logout-campaign-pending">
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        Ending…
                      </span>
                    </Button>
                  ) : null}
                  {presenceState === PRESENCE_STATE.STARTING ? (
                    <Button type="button" disabled data-testid="workspace-start-dialing-pending">
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        Starting…
                      </span>
                    </Button>
                  ) : null}
                  {presenceState === PRESENCE_STATE.ACTIVE ? (
                    <Button type="button" onClick={handlePause} data-testid="workspace-pause-button">
                      Pause
                    </Button>
                  ) : null}
                  {presenceState === PRESENCE_STATE.PAUSING ? (
                    <Button type="button" disabled data-testid="workspace-pause-pending">
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        Pausing…
                      </span>
                    </Button>
                  ) : null}
                  {presenceState === PRESENCE_STATE.PAUSED ? (
                    <Button type="button" onClick={handleResume} data-testid="workspace-resume-button">
                      Resume
                    </Button>
                  ) : null}

                  {/*
                    PR-A. Hang Up and Hanging Up moved to the persistent
                    bottom call-control region (`WorkspaceCallBar`), where
                    WORKSPACE-FEATURE-001 S15.1/S15.7 place them. The
                    elements, their testids, their handler and their
                    eligibility are unchanged -- the shell still decides
                    when each renders and passes that decision down.

                    Pause/Resume stay here, in the campaign region: S15.2
                    requires call condition and campaign participation to
                    remain separately understandable, and native refuses
                    to pause an INCALL agent regardless.
                  */}
                </div>
              }
              notice={
                /*
                  Status notices and failures. They wrap onto their own
                  full-width line beneath the row rather than competing
                  with the controls for the right edge, which is what
                  keeps a long error from relocating a control (S5.4:
                  a state change alters content without relocating a
                  region).
                */
                <div className="flex w-full flex-col gap-2" data-testid="workspace-operational-notices">

                  {/*
                    AW-2B3. Stranded retained lead. No disposition panel
                    renders here and none may: the panel below is gated on
                    WRAP_UP, and the backend offers no choices outside it.
                    The notice names the supervisor follow-up because that
                    is the only true next step, and names campaign logout
                    because a state with no exit is its own defect.
                  */}
                  {presenceState === PRESENCE_STATE.RETAINED_LEAD_STRANDED ? (
                    <p
                      className="text-xs leading-snug text-slate-400"
                      data-testid="workspace-retained-lead-stranded-notice"
                    >
                      {STRANDED_RETAINED_LEAD_NOTICE}
                    </p>
                  ) : null}

                  {/*
                    Wrap-up exposes no control at all. Start Dialing, Resume
                    and Pause are all absent: the agent is natively PAUSED
                    with the lead still held, and BB-12 owns disposition and
                    the exit from this state.
                  */}
                  {presenceState === PRESENCE_STATE.WRAP_UP ? (
                    <p
                      className="text-xs leading-snug text-slate-400"
                      data-testid="workspace-wrap-up-notice"
                    >
                      {leadRetained
                        ? "Call ended. This lead is still assigned to you and is awaiting completion."
                        : "Call ended. Awaiting completion."}
                    </p>
                  ) : null}

                  {/*
                    PR-A. The disposition panel moved to the persistent
                    bottom call-control region (`WorkspaceCallBar`), where
                    WORKSPACE-FEATURE-001 S15.9 places call completion.
                    Every element, testid, handler, disabled rule and
                    string is unchanged, and the shell still owns the
                    Wrap-up gate, the options it fetched, the in-flight
                    lock and the D3 semantics behind `handleDisposition`.
                  */}

              {/*
                Autoplay refused. The SIP/media leg is untouched and
                still live -- this is a browser gesture requirement, not
                a media failure, so it gets a bounded action rather than
                a teardown.
              */}
              {playbackBlocked ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={enableAudio}
                    data-testid="workspace-enable-audio-button"
                  >
                    Enable Audio
                  </Button>
                  <p
                    className="text-xs leading-snug text-amber-300"
                    data-testid="workspace-enable-audio-notice"
                  >
                    {ENABLE_AUDIO_PROMPT}
                  </p>
                </>
              ) : null}

              {/*
                BB-14d2c2 media loss after Product Ready. The agent is
                told plainly; native presence is deliberately left
                alone, because the automatic fail-safe pause is BB-14e's
                and inventing it here would be this slice deciding a
                policy it was told not to own. What it must never do is
                keep presenting healthy audio -- so this notice is
                unconditional once the agent has passed Ready.
              */}
              {audioLostAfterReady ? (
                <p
                  className="text-xs leading-snug text-red-300"
                  role="alert"
                  data-testid="workspace-audio-lost"
                >
                  {audioRecoveryRequired ? AUDIO_RECOVERY_NOTICE : AUDIO_LOST_NOTICE}
                </p>
              ) : null}

              {audioFailure && presenceState === PRESENCE_STATE.AUDIO_FAILED ? (
                <p
                  className="text-xs leading-snug text-red-300"
                  role="alert"
                  data-testid="workspace-audio-failure"
                >
                  {audioFailureMessage(audioFailure)}
                </p>
              ) : null}

              {callError ? (
                <p
                  className="text-xs leading-snug text-red-300"
                  role="alert"
                  data-testid="workspace-call-failure"
                >
                  {callError}
                </p>
              ) : null}

              {presenceError ? (
                <p
                  className="text-xs leading-snug text-red-300"
                  role="alert"
                  data-testid="workspace-presence-failure"
                >
                  {presenceError}
                </p>
              ) : null}
                </div>
              }
            />
          }
          body={
            <WorkspaceProductionLayout
              center={
                /*
                 * AW-2B3.2, re-hosted. The real Script runner, now
                 * inside the intended center tab region rather than
                 * being the whole panel slot.
                 *
                 * Still no lead or customer data, no fixture content
                 * and no provider identifier: the runner receives one
                 * value -- the call phase this shell already owns --
                 * and everything else it needs comes from the
                 * authoritative run-start. Canonical Lead context
                 * remains AW-2C.
                 *
                 * `WorkspaceCenterTabs` mounts the runner permanently
                 * (S10.4/S10.6). That is layout stability only: the
                 * runner's own `shouldStartRun` is unchanged and still
                 * admits exactly one phase, authoritative CONNECTED.
                 */
                <WorkspaceMainCard
                  className="flex-1"
                  activeTab={activeCenterTab}
                  onActiveTabChange={setActiveCenterTab}
                  tabs={[
                    {
                      value: "lead-details",
                      label: "Lead Details",
                      Icon: User,
                      testId: "workspace-tab-lead-details",
                      panelTestId: "workspace-lead-details-region",
                      content: (
                        <div className={DEFERRED_PANEL_CLASS}>
                          <p className="max-w-prose text-sm text-slate-400">
                            {LEAD_DETAILS_DEFERRED}
                          </p>
                        </div>
                      ),
                    },
                    {
                      value: "script",
                      label: "Script",
                      Icon: FileText,
                      testId: "workspace-tab-script",
                      panelTestId: "workspace-script-region",
                      // S10.4/S10.6: the Script host is permanent. Removing
                      // this reintroduces per-tab-switch remounting of the
                      // runner, which destroys the run the agent is on.
                      forceMount: true,
                      content: (
                        <WorkspaceScriptRunner
                          phase={presenceState}
                          onRunChange={handleRunChange}
                          customer={customerData}
                        />
                      ),
                    },
                    {
                      value: "quoting",
                      label: "Quoting",
                      Icon: ReceiptText,
                      testId: "workspace-tab-quoting",
                      panelTestId: "workspace-quoting-region",
                      /*
                        AW-ORIGINAL-RESTORE-002. The ORIGINAL Quoting
                        content region, mounted whole.

                        It is the one original panel that needed no
                        eligibility work at all: it imports `react` and
                        one icon, takes no props, holds no state, issues
                        no request, and renders no customer data -- it
                        has always been the intended host for a future
                        quoting application rather than a view of
                        anything. So the bespoke deferred paragraph that
                        stood in for it is gone, and the original says
                        what it has always said.
                      */
                      content: <QuotingPanel />,
                    },
                  ]}
                />
              }
              context={
                /*
                 * AW-VIS-3. The rail's presentation state, forwarded.
                 *
                 * The dock is still pure presentation and still holds
                 * nothing: the shell owns both values, the same way the
                 * authoritative original shell does. Seven of the eight
                 * panels remain honest deferred copy -- selecting a tool
                 * opens its panel and claims no capability.
                 */
                <WorkspaceContextDock
                  activeTool={activeTool}
                  onSelectTool={handleSelectTool}
                  isExpanded={isToolsExpanded}
                  onToggleExpanded={handleToggleTools}
                  toolData={toolData}
                  customerData={customerData}
                />
              }
            />
          }
          callBar={
            /*
             * PR-A. Hang Up and disposition, re-hosted verbatim.
             *
             * The shell keeps every decision: which controls are
             * eligible, which options the backend offered, whether a
             * disposition is in flight, and what `handleHangup` /
             * `handleDisposition` do. The bar receives booleans and
             * callbacks and renders them.
             */
            <WorkspaceCallBar
              conditionLabel={callConditionLabel(presenceState)}
              showHangup={presenceState === PRESENCE_STATE.CONNECTED}
              showHangupPending={presenceState === PRESENCE_STATE.HANGING_UP}
              showDisposition={presenceState === PRESENCE_STATE.WRAP_UP}
              onHangup={handleHangup}
              dispositionOptions={dispositionOptions}
              dispositionInFlight={dispositionInFlight}
              onDisposition={handleDisposition}
              pauseAfterDisposition={pauseAfterDisposition}
              onPauseAfterDispositionChange={setPauseAfterDisposition}
              dispositionError={dispositionError}
            />
          }
        />
      ) : sessionResolved ? (
        <WorkspacePreviewGate onLogin={handleLogin} />
      ) : (
        /*
         * P2-R1 -- restoration has not answered yet.
         *
         * Deliberately not the Campaign Login gate: rendering an
         * actionable Login button before we know whether a session
         * exists is precisely how an agent issues a second login over a
         * live one. This resolves within one request and then falls
         * through to whichever branch is correct.
         */
        <section
          aria-label="Restoring session"
          className="flex flex-1 flex-col items-center justify-center gap-3 px-4 py-16 text-sm text-slate-400"
          data-testid="workspace-session-resolving"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Restoring your session…
        </section>
      )}
    </main>
  );
}
